import { useState, useEffect } from "react"
import { toast } from "@/src/common/snackbar"
import { Iconify } from "@/src/common/iconify"
import { DashboardContent } from "@/src/layouts/dashboard"
import { useInternalStore } from "@/src/store/slices/internal.slice"
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query"
import {
    getWorkspaceTeamManagers,
    assignRecruitersToManager,
    type InternalTeamMember,
    getWorkspaceTeamRecruiters,
} from "@/src/services/internal"

import Box from "@mui/material/Box"
import Card from "@mui/material/Card"
import Chip from "@mui/material/Chip"
import Alert from "@mui/material/Alert"
import Stack from "@mui/material/Stack"
import Table from "@mui/material/Table"
import { LoadingButton } from "@mui/lab"
import Checkbox from "@mui/material/Checkbox"
import MenuItem from "@mui/material/MenuItem"
import TableRow from "@mui/material/TableRow"
import TableBody from "@mui/material/TableBody"
import TableCell from "@mui/material/TableCell"
import TableHead from "@mui/material/TableHead"
import TextField from "@mui/material/TextField"
import Typography from "@mui/material/Typography"
import Autocomplete from "@mui/material/Autocomplete"
import TableContainer from "@mui/material/TableContainer"
import CircularProgress from "@mui/material/CircularProgress"

const createManagersQueryKey = (workspaceId: string) => ["internal", "team-managers", workspaceId] as const
const createRecruitersQueryKey = (workspaceId: string) => ["internal", "team-recruiters", workspaceId] as const

export default function TeamAssignments() {
    const queryClient = useQueryClient()
    const { workspaces, loading: workspacesLoading, fetchWorkspaces } = useInternalStore()
    const [selectedWorkspaceId, setSelectedWorkspaceId] = useState("")
    const [selectedManager, setSelectedManager] = useState<InternalTeamMember | null>(null)
    const [selectedRecruiters, setSelectedRecruiters] = useState<InternalTeamMember[]>([])

    useEffect(() => {
        fetchWorkspaces({ page: 0, size: 100 })
    }, [fetchWorkspaces])

    const managersQuery = useQuery({
        queryKey: createManagersQueryKey(selectedWorkspaceId),
        queryFn: () => getWorkspaceTeamManagers(selectedWorkspaceId),
        enabled: Boolean(selectedWorkspaceId),
    })

    const recruitersQuery = useQuery({
        queryKey: createRecruitersQueryKey(selectedWorkspaceId),
        queryFn: () => getWorkspaceTeamRecruiters(selectedWorkspaceId),
        enabled: Boolean(selectedWorkspaceId),
    })

    const managers = managersQuery.data?.data ?? []
    const recruiters = recruitersQuery.data?.data ?? []
    const teamMembersLoading = managersQuery.isLoading || recruitersQuery.isLoading
    const teamMembersFailed = managersQuery.isError || recruitersQuery.isError

    const assignmentMutation = useMutation({
        mutationFn: () =>
            assignRecruitersToManager(selectedWorkspaceId, {
                managerId: selectedManager?.userId ?? "",
                recruiterIds: selectedRecruiters.map((recruiter) => recruiter.userId),
            }),
        onSuccess: async (apiResponse) => {
            await queryClient.invalidateQueries({ queryKey: createRecruitersQueryKey(selectedWorkspaceId) })
            setSelectedRecruiters([])
            toast.success(
                `${apiResponse.data.updated} recruiter${apiResponse.data.updated === 1 ? "" : "s"} assigned successfully`
            )
        },
    })

    const selectWorkspace = (workspaceId: string) => {
        setSelectedWorkspaceId(workspaceId)
        setSelectedManager(null)
        setSelectedRecruiters([])
    }

    const submitManagerAssignment = () => {
        if (!selectedManager || selectedRecruiters.length === 0) return
        assignmentMutation.mutate()
    }

    return (
        <DashboardContent sx={{ flexGrow: 1, display: "flex", flexDirection: "column", pb: 3, mt: 5 }}>
            <Box sx={{ mb: 3 }}>
                <Typography variant="h4" fontWeight={700}>
                    Team Assignment
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    Assign one or more recruiters to a manager within a workspace.
                </Typography>
            </Box>

            <Card sx={{ p: 3, mb: 3, border: 1, borderColor: "divider", borderRadius: 2 }}>
                <Stack spacing={3}>
                    <TextField
                        select
                        fullWidth
                        label="Workspace"
                        value={selectedWorkspaceId}
                        onChange={(changeEvent) => selectWorkspace(changeEvent.target.value)}
                        disabled={workspacesLoading}
                        helperText="Select a workspace to load its managers and recruiters."
                    >
                        {workspaces.map((workspace) => (
                            <MenuItem key={workspace.id} value={workspace.id}>
                                {workspace.name}
                            </MenuItem>
                        ))}
                    </TextField>

                    {selectedWorkspaceId && teamMembersFailed && (
                        <Alert severity="error">The team members could not be loaded. Please try again.</Alert>
                    )}

                    {selectedWorkspaceId && teamMembersLoading ? (
                        <Stack alignItems="center" sx={{ py: 4 }}>
                            <CircularProgress size={32} />
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
                                Loading team members...
                            </Typography>
                        </Stack>
                    ) : (
                        selectedWorkspaceId && (
                            <>
                                <Autocomplete
                                    options={managers}
                                    value={selectedManager}
                                    onChange={(_changeEvent, manager) => setSelectedManager(manager)}
                                    isOptionEqualToValue={(managerOption, managerValue) =>
                                        managerOption.userId === managerValue.userId
                                    }
                                    getOptionLabel={(manager) => manager.fullName}
                                    noOptionsText="No managers found in this workspace"
                                    renderInput={(inputParameters) => (
                                        <TextField
                                            {...inputParameters}
                                            label="Manager"
                                            placeholder="Search for a manager"
                                        />
                                    )}
                                    renderOption={(optionProperties, manager) => (
                                        <Box component="li" {...optionProperties} key={manager.userId}>
                                            <Stack>
                                                <Typography variant="body2" fontWeight={600}>
                                                    {manager.fullName}
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary">
                                                    {manager.email}
                                                </Typography>
                                            </Stack>
                                        </Box>
                                    )}
                                />

                                <Autocomplete
                                    multiple
                                    disableCloseOnSelect
                                    options={recruiters}
                                    value={selectedRecruiters}
                                    onChange={(_changeEvent, recruiterList) => setSelectedRecruiters(recruiterList)}
                                    isOptionEqualToValue={(recruiterOption, recruiterValue) =>
                                        recruiterOption.userId === recruiterValue.userId
                                    }
                                    getOptionLabel={(recruiter) => recruiter.fullName}
                                    noOptionsText="No recruiters found in this workspace"
                                    renderTags={(recruiterList, getTagProperties) =>
                                        recruiterList.map((recruiter, recruiterIndex) => (
                                            <Chip
                                                {...getTagProperties({ index: recruiterIndex })}
                                                key={recruiter.userId}
                                                label={recruiter.fullName}
                                                size="small"
                                            />
                                        ))
                                    }
                                    renderOption={(optionProperties, recruiter, optionState) => (
                                        <Box component="li" {...optionProperties} key={recruiter.userId}>
                                            <Checkbox checked={optionState.selected} sx={{ mr: 1 }} />
                                            <Stack>
                                                <Typography variant="body2" fontWeight={600}>
                                                    {recruiter.fullName}
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary">
                                                    {recruiter.email}
                                                    {recruiter.reportingManagerName
                                                        ? ` · Manager: ${recruiter.reportingManagerName}`
                                                        : " · Unassigned"}
                                                </Typography>
                                            </Stack>
                                        </Box>
                                    )}
                                    renderInput={(inputParameters) => (
                                        <TextField
                                            {...inputParameters}
                                            label="Recruiters"
                                            placeholder={
                                                selectedRecruiters.length === 0 ? "Search and select recruiters" : ""
                                            }
                                        />
                                    )}
                                />

                                <Stack direction="row" justifyContent="flex-end">
                                    <LoadingButton
                                        variant="contained"
                                        loading={assignmentMutation.isPending}
                                        disabled={!selectedManager || selectedRecruiters.length === 0}
                                        startIcon={<Iconify icon="mdi:account-switch-outline" />}
                                        onClick={submitManagerAssignment}
                                    >
                                        Assign Recruiters
                                    </LoadingButton>
                                </Stack>
                            </>
                        )
                    )}
                </Stack>
            </Card>

            {selectedWorkspaceId && !teamMembersLoading && !teamMembersFailed && (
                <Card sx={{ overflow: "hidden", border: 1, borderColor: "divider", borderRadius: 2 }}>
                    <Box sx={{ p: 2.5, borderBottom: 1, borderColor: "divider" }}>
                        <Typography variant="h6">Current Recruiter Assignments</Typography>
                        <Typography variant="body2" color="text.secondary">
                            {recruiters.length} recruiter{recruiters.length === 1 ? "" : "s"} in this workspace
                        </Typography>
                    </Box>
                    <TableContainer>
                        <Table>
                            <TableHead>
                                <TableRow sx={{ bgcolor: "grey.50" }}>
                                    <TableCell sx={{ fontWeight: 600 }}>Recruiter</TableCell>
                                    <TableCell sx={{ fontWeight: 600 }}>Email</TableCell>
                                    <TableCell sx={{ fontWeight: 600 }}>Assigned Manager</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {recruiters.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={3} align="center" sx={{ py: 5 }}>
                                            <Typography variant="body2" color="text.secondary">
                                                No recruiters found in this workspace.
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    recruiters.map((recruiter) => (
                                        <TableRow key={recruiter.userId} hover>
                                            <TableCell>
                                                <Typography variant="body2" fontWeight={600}>
                                                    {recruiter.fullName}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="body2" color="text.secondary">
                                                    {recruiter.email}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                {recruiter.reportingManagerName ? (
                                                    <Chip
                                                        label={recruiter.reportingManagerName}
                                                        color="primary"
                                                        variant="outlined"
                                                        size="small"
                                                    />
                                                ) : (
                                                    <Chip label="Unassigned" variant="outlined" size="small" />
                                                )}
                                            </TableCell>
                                        </TableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Card>
            )}
        </DashboardContent>
    )
}
